import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import RadarChartOutlined from "@ant-design/icons/RadarChartOutlined";
import { forwardRef } from "react";

import { Box } from "../box";
import { BodyHeader } from "../body-header";
import { BodyIcon } from "../body-icon";
import { BodyText } from "../body-text";
import { Spotlight } from "../../spotlight";
import { Step } from "../guide";
import { useOnClick } from "../use-on-click";
import { useOnExists } from "../use-on-exists";
import { useOnItemsCreated } from "../use-on-items-created";

const Guide = (props) => {
  const { guide, isComplete, accounts = [], patchSetupState } = props;

  const steps = [
    {
      allowClicks: true,
      tooltip: {
        content: (
          <Step onClose={guide.stop}>
            This is how you get to your accounts, click here to go there now.
          </Step>
        ),
        placement: "right",
      },
      elementId: "accounts-menu-item",
    },
    {
      tooltip: {
        content: (
          <Step onNext={guide.forward} onClose={guide.stop}>
            Here is where your accounts will show up after you create them.
          </Step>
        ),
        placement: "top",
      },
      elementId: "ACCOUNT-view-table",
    },
    {
      allowClicks: true,
      tooltip: {
        content: (
          <Step onBack={guide.back} onNext={guide.forward} onClose={guide.stop}>
            This is the primary way to create accounts, click to add one now, or
            continue to use the import tool.
          </Step>
        ),
        placement: "bottom",
      },
      elementId: "create-account-button",
    },
    {
      allowClicks: true,
      tooltip: {
        content: (
          <Step
            onBack={guide.back}
            onNext={() => {
              if (!isComplete) {
                patchSetupState.mutate({
                  isAccountsStepComplete: true,
                });
              }

              guide.go(steps.length);
            }}
            nextText={"Got it"}
            onClose={guide.stop}
          >
            Click here to import your account data if you are coming from
            another system.
          </Step>
        ),
        placement: "bottom",
      },
      elementId: "import-ACCOUNT-button",
    },
  ];

  useOnExists(guide.forward, guide.index === 0 ? "ACCOUNT-view-table" : null);

  useOnClick(
    () => guide.go(steps.length),
    guide.index === 2 ? "create-account-button" : null
  );

  useOnClick(
    () => guide.go(steps.length),
    guide.index === 3 ? "import-ACCOUNT-button" : null
  );

  useOnItemsCreated(guide.complete, accounts);

  const step = steps[guide.index];

  if (!step) {
    return null;
  }

  return (
    <Spotlight
      allowClicks={step.allowClicks}
      elementId={step.elementId}
      tooltip={step.tooltip}
    />
  );
};

const HeroButton = (props, ref) => {
  const { isComplete, isCurrent, onClick } = props;

  const bodyText = (
    <>
      Accounts are the companies that you are providing your products and
      services to.
    </>
  );

  return (
    <Box
      isComplete={isComplete}
      isCurrent={isCurrent}
      onClick={onClick}
      ref={ref}
    >
      <BodyIcon
        Icon={RadarChartOutlined}
        isComplete={isComplete}
        isCurrent={isCurrent}
      />
      <BodyHeader
        isComplete={isComplete}
        isCurrent={isCurrent}
        title={"Accounts"}
      />
      <BodyText isComplete={isComplete} isCurrent={isCurrent}>
        {bodyText}
      </BodyText>
    </Box>
  );
};

const getStartIndex = () => {
  if ((window.location.href || "").endsWith("/accounts")) {
    return 1;
  }

  return 0;
};

export const accounts = {
  Guide,
  HeroButton: forwardRef(HeroButton),

  getStartIndex,
  isComplete: ({ accounts, organization }) =>
    !isEmpty(accounts) ||
    get(organization, "setupState.isAccountsStepComplete"),
  mapData: ({ accounts }) => ({ accounts }),
  type: "account",
};
